import { setAutosaveScope } from '@/core/api/autosaveScope'
import { fetchMe, switchVariant, type Variant, type Me as AuthSession } from '@/core/api/auth'
import { assertWriteIdentity, captureWriteIdentity, currentActiveOrganisationId, setActiveOrganisationId, type WriteIdentity } from '@/core/api/http'
import { apiListAll } from '@/core/api/resources'
import { isPortalSurface } from '@/core/lib/appSurface'
import { loadPortalContext } from '@/core/data/participantAuth'

export type Membership = {
  organisationId: string
  organisationName: string
  isOwner: boolean
  isCoach: boolean
  variant: string
  variantsOffered: string[]
}

export type Me = Membership & {
  userId: string
  vorname: string
  nachname: string
  email: string
  memberships: Membership[]
  variants: Variant[]
  hatPasswort: boolean
}

let cache: Promise<Me> | null = null
let resolvedMe: Me | null = null
const identityListeners = new Set<(identity: Me) => void>()
type ProfileName = Pick<Me, 'vorname' | 'nachname'>
let profileNameEdit: { userId: string; name: ProfileName; pending: boolean } | null = null

export function subscribeMe(listener: (identity: Me) => void): () => void {
  identityListeners.add(listener)
  return () => { identityListeners.delete(listener) }
}

export function updateMeProfileName(userId: string, name: ProfileName, pending: boolean): void {
  if (resolvedMe && resolvedMe.userId !== userId) return
  profileNameEdit = { userId, name, pending }
  if (!resolvedMe) return
  resolvedMe = { ...resolvedMe, ...name }
  cache = Promise.resolve(resolvedMe)
  whoCache = null
  for (const listener of identityListeners) listener(resolvedMe)
}

export function updateMeOrganisationName(organisationId: string, name: string): void {
  if (!resolvedMe) return
  const memberships = resolvedMe.memberships.map((membership) => membership.organisationId === organisationId
    ? { ...membership, organisationName: name }
    : membership).sort((a, b) => a.organisationName.localeCompare(b.organisationName, 'de', { sensitivity: 'base' }))
  resolvedMe = {
    ...resolvedMe,
    memberships,
    ...(resolvedMe.organisationId === organisationId ? { organisationName: name } : {}),
  }
  cache = Promise.resolve(resolvedMe)
  whoCache = null
  for (const listener of identityListeners) listener(resolvedMe)
}

export function me(): Promise<Me> {
  return cached(fetchMe)
}

function cached(read: () => Promise<AuthSession | null>): Promise<Me> {
  if (cache) return cache
  const beforeRead = profileNameEdit
  const pending: Promise<Me> = resolve(read, () => cache === pending)
    .then((m) => {
      if (cache !== pending) throw new Error("Die Sitzungsabfrage wurde durch eine neuere Sitzung ersetzt.")
      const edit = profileNameEdit
      if (edit?.userId === m.userId && (edit.pending || beforeRead?.pending || edit !== beforeRead)) {
        m = { ...m, ...edit.name }
      }
      resolvedMe = m
      return m
    })
    .catch((e: unknown) => {
      if (cache === pending) cache = null
      throw e
    })
  cache = pending
  return pending
}

type BootstrapRead = { userId: string; organisationId: string }

export function meAfterBootstrap(session: AuthSession, boot: BootstrapRead): Promise<Me> {
  const matchesBootstrap = session.member_id === boot.userId &&
    session.organisations.some((o) => o.organisation_id === boot.organisationId)
  return matchesBootstrap ? cached(async () => session) : me()
}

export function currentVariant(): string {
  if (resolvedMe === null) {
    throw new Error("currentVariant(): no session resolved yet - call/await me() before reading the active variant")
  }
  return resolvedMe.variant
}

export function resetMe(): void {
  setAutosaveScope(null)
  cache = null
  resolvedMe = null
  whoCache = null
  whoCacheCoachingId = null
}

function activeOrgKey(authUserId: string): string {
  return `admination.azav.currentOrgId.${authUserId}`
}

function resolveActiveMembership(authUserId: string, memberships: readonly Membership[]): Membership {
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(activeOrgKey(authUserId)) : null
  const match = stored ? memberships.find((m) => m.organisationId === stored) : undefined
  return match ?? memberships[0]
}

export async function setActiveOrganisation(organisationId: string): Promise<WriteIdentity> {
  const identity = captureWriteIdentity()
  const session = await fetchMe()
  assertWriteIdentity(identity)
  if (session) localStorage.setItem(activeOrgKey(session.user_id), organisationId)
  setActiveOrganisationId(organisationId)
  resetMe()
  return captureWriteIdentity()
}

export async function setActiveVariant(organisationId: string, variantKey: string): Promise<WriteIdentity> {
  const identity = await switchVariant(organisationId, variantKey)
  assertWriteIdentity(identity)
  resetMe()
  return captureWriteIdentity()
}

export function variantDriftedFrom(session: AuthSession): boolean {
  if (resolvedMe === null) return false
  const organisationId = currentActiveOrganisationId()
  const active = session.organisations.find((o) => o.organisation_id === organisationId)
  return !!active && active.effective_variant !== resolvedMe.variant
}

async function resolve(read: () => Promise<AuthSession | null>, isCurrent: () => boolean): Promise<Me> {
  const session = await read()
  if (!session) throw new Error("not authenticated")
  if (session.member_id == null) throw new Error("no member row for this identity")

  const memberships = session.organisations
    .map((o) => ({
      organisationId: o.organisation_id,
      organisationName: o.name,
      isOwner: o.is_owner,
      isCoach: o.is_coach,
      variant: o.effective_variant,
      variantsOffered: o.variants_offered,
    }))
    .sort((a, b) => a.organisationName.localeCompare(b.organisationName, 'de', { sensitivity: 'base' }))
  if (memberships.length === 0) throw new Error("no membership found")

  const active = resolveActiveMembership(session.user_id, memberships)
  if (!isCurrent()) throw new Error("Die Sitzungsabfrage wurde durch eine neuere Sitzung ersetzt.")
  setActiveOrganisationId(active.organisationId)
  setAutosaveScope(JSON.stringify([session.user_id, active.organisationId, active.variant]))

  return {
    userId: session.member_id,
    organisationId: active.organisationId,
    organisationName: active.organisationName,
    isOwner: active.isOwner,
    isCoach: active.isCoach,
    vorname: session.vorname ?? '',
    nachname: session.nachname ?? '',
    email: session.email ?? '',
    memberships,
    variant: active.variant,
    variantsOffered: active.variantsOffered,
    variants: session.variants,
    hatPasswort: session.hat_passwort,
  }
}

export type MemberIdentity = Me & { kind: 'member' }

export type ParticipantIdentity = {
  kind: 'participant'
  teilnehmendeId: string
  coachingIds: string[]
}

export type Identity = MemberIdentity | ParticipantIdentity

let whoCache: Promise<Identity> | null = null
let whoCacheCoachingId: string | null = null

export function whoAmI(): Promise<Identity> {
  const key = isPortalSurface ? (loadPortalContext()?.coachingId ?? null) : null
  if (whoCache && key !== whoCacheCoachingId) whoCache = null
  whoCacheCoachingId = key
  if (!whoCache) {
    const pending = resolveIdentityUnion().catch((e: unknown) => {
      if (whoCache === pending) whoCache = null
      throw e
    })
    whoCache = pending
  }
  return whoCache
}

async function resolveIdentityUnion(): Promise<Identity> {
  if (isPortalSurface) return resolveParticipant(await fetchMe(), loadPortalContext()?.coachingId ?? null)
  const m = await me()
  return { kind: 'member', ...m }
}

export async function resolveParticipant(
  session: Pick<AuthSession, 'user_id'> | null,
  coachingId: string | null,
): Promise<ParticipantIdentity> {
  if (!session) {
    throw new Error("Kein aktiver Zugang - bitte den Portal-Link verwenden.")
  }
  const rows = await apiListAll('/teilnehmende', {
    auth_user_id: session.user_id,
    include: coachingId ? ['teilnahme'] : undefined,
  })
  const matches = coachingId ? rows.filter((row) =>
    (row.teilnahme ?? []).some((ta) => ta.coaching_id === coachingId),
  ) : rows
  if (matches.length !== 1) throw new Error("Kein Teilnehmerprofil gefunden - bitte den Portal-Link verwenden.")
  const [t] = matches
  const tas = await apiListAll('/teilnahme', {
    teilnehmende_id: t.id,
    order: 'coaching_id',
  })
  return {
    kind: 'participant',
    teilnehmendeId: t.id,
    coachingIds: tas.map((r) => r.coaching_id),
  }
}
